// react
import { lazy, Suspense, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

// feature
import { ToastContainer } from 'react-toastify';
import CustomTheme from './Feature/CustomTheme';
import LayoutPanel from './components/LayoutPanel';
import LayoutPlayground from './components/LayoutPlayground';
import MenuHelp from './components/MenuHelp';
// style
import './assets/style/App.css';
import 'react-toastify/dist/ReactToastify.css';

// component page - eagerly loaded (needed on initial routes or lightweight)
import Login from './pages/Login';
import SingUp from './pages/SingUp';
import ForgotPassword from './pages/ForgotPassword';
import CreateNewPassword from './pages/CreateNewPassword';
import Callback from './pages/Callback';
import Verification from './pages/Verification';
import ConfirmEmail from './pages/ConfirmEmail';
import PrivateRoute from './Auth/PrivateRoute';
import Stepper from './pages/Stepper';
import StepperLoading from './pages/Stepper/StepperLoading';
import NotFound from './pages/NotFound';
import { useConfig } from './Hooks/useConfig';
import HomePlayground from './pages/HomePlayground';

import InternalError from './pages/InternalError';
import PrivateRouteAndStepper from './Auth/PrivateRouteAndStepper';
import useUsersFacade from './store/profile/useUsersFacade';
import { useAuthStatus } from './store/auth/useAuthStatus';
import Updating from './pages/Updating';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { getCookie, setCookie } from './utils/cookies';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import LightThemeWrapper from './components/LightThemeWrapper';
import CreditsBalanceModalContainer from './components/CreditsBalanceModal/CreditsBalanceModalContainer';
import useCreditWarningStore from './store/credits/useCreditWarningStore';
import { useTheme } from '@mui/material';
import SeoRobots from './components/SeoRobots';

// Lazy loaded pages - split into separate chunks
const ResumeScore = lazy(() => import('./pages/ResumeScore'));
const ResumeParser = lazy(() => import('./pages/ResumeParser'));
const ResumeReview = lazy(() => import('./pages/ResumeReview'));
const LinkedinProfile = lazy(() => import('./pages/LinkedinProfile'));
const LinkedinCompany = lazy(() => import('./pages/LinkedinCompany'));
const HomePanel = lazy(() => import('./pages/HomePanel'));
const Payment = lazy(() => import('./pages/Payment'));
const PaymentStatus = lazy(() => import('./pages/PaymentStatus'));
const Profile = lazy(() => import('./pages/Profile'));
const ManualInvoices = lazy(() => import('./pages/ManualInvoices'));
const Usage = lazy(() => import('./pages/Usage'));
const ApiInfo = lazy(() => import('./pages/ApiInfo'));
const GetMoreCreditsPayment = lazy(() => import('./pages/GetMoreCreditsPayment'));
const Billing = lazy(() => import('./pages/Billing'));
const BaseButtonTest = lazy(() => import('./pages/BaseButtonTest'));

function App() {
  const { pathname } = useLocation();
  const { panelUrl, stateProject, recaptchaSiteKey, baseDomain } = useConfig();
  const theme = useTheme();
  const history = useHistory();

  const [isIntervalActive, setIsIntervalActive] = useState(true);

  // zustand
  const { userInfo, fetchUsers } = useUsersFacade();
  const { isLogin, loginState } = useAuthStatus();
  const { openModal } = useCreditWarningStore();

  useEffect(() => {
    if (isLogin() && userInfo) {
      const creditsRemaining = (userInfo?.credits ?? 0) + (userInfo?.extra_credits ?? 0);
      if (creditsRemaining < 0) {
        const hasShown = getCookie('credit_warning_login_shown');
        if (!hasShown) {
          openModal();
          setCookie('credit_warning_login_shown', 'true', 24 * 3600, baseDomain);
        }
      }
    }
  }, [loginState, userInfo, openModal, baseDomain]);

  useEffect(() => {
    if (isLogin()) {
      const showStepper = getCookie('showStepper');

      if (showStepper === 'true') {
        history.push('/stepper');
      }

      fetchUsers();
    }
  }, [loginState, fetchUsers]);

  // function
  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [pathname]);

  useEffect(() => {
    if (userInfo && !userInfo.trial_used) {
      localStorage.setItem('first_time_trial', userInfo.trial_used);
    }
  }, [userInfo]);

  const project = panelUrl.includes(window.location.hostname)
    ? 'panel'
    : 'playground';

  // Exclude payment routes from stepper loading to prevent infinite loops
  const isPaymentRoute =
    pathname.startsWith('/payment/status/') ||
    pathname.startsWith('/payment/') ||
    pathname.startsWith('/get-more-credits/payment/');

  useEffect(() => {
    if (panelUrl.includes(window.location.hostname)) {
      document.title = 'MagicalAPI | Panel';
    } else if (!panelUrl.includes(window.location.hostname)) {
      document.title = 'MagicalAPI | Playground';
    }
  }, [isIntervalActive]);

  useEffect(() => {
    const updateBadgeVisibility = () => {
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) {
        if (
          location.pathname === '/login' ||
          location.pathname === '/sign-up'
        ) {
          badge.style.visibility = 'visible';
        } else {
          badge.style.visibility = 'hidden';
        }
        return true; // Badge found and handled
      }
      return false; // Badge not found
    };

    // Check for the badge initially
    if (updateBadgeVisibility()) {
      return; // If badge is already handled, skip observer setup
    }

    // Observe DOM for changes
    const observer = new MutationObserver(() => {
      if (updateBadgeVisibility()) {
        observer.disconnect(); // Disconnect after handling the badge
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [location.pathname]);

  const handleCancled = () => {
    setIsIntervalActive(false);
    document.title = 'MagicalAPI | Playground';
  };

  useEffect(() => {
    console.log(
      `%c
    __  __                _              _             _____  _____ 
   |  \\/  |              (_)            | |     /\\    |  __ \\|_   _|
   | \\  / |  __ _   __ _  _   ___  __ _ | |    /  \\   | |__) | | |  
   | |\\/| | / _\` | / _\` || | / __|/ _\` || |   / /\\ \\  |  ___/  | |  
   | |  | || (_| || (_| || || (__| (_| || |  / ____ \\ | |     _| |_ 
   |_|  |_| \\__,_| \\__, ||_| \\___|\\__,_||_| /_/    \\_\\|_|    |_____|
                    __/ |                                           
                   |___/                                       
`,
      'color: #4338CA;'
    );
    console.log(
      '%c--------------------------------------------------------------',
      'color: #4338CA;'
    );
    console.log(
      '%cIt seems you are a developer, you can use our API : https://docs.magicalapi.com/',
      'color: #4338CA;'
    );
    console.log(
      '%c--------------------------------------------------------------',
      'color: #4338CA;'
    );
  }, []);

  if (stateProject === 'updating') {
    return (
      <CustomTheme>
        <SeoRobots />
        <Updating />
      </CustomTheme>
    );
  }

  return (
    <CustomTheme>
      <SeoRobots />
      <Switch>
        <Route path="/login" exact>
          <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
            <Login />
          </GoogleReCaptchaProvider>
        </Route>
        <Route exact path="/sign-up">
          {loginState ? (
            <Redirect to="/" />
          ) : (
            <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
              <SingUp />
            </GoogleReCaptchaProvider>
          )}
        </Route>
          <Route exact path="/forgot-password">
            {loginState ? <Redirect to="/" /> : <ForgotPassword />}
          </Route>
          <Route exact path="/reset-password">
            {loginState ? <Redirect to="/" /> : <CreateNewPassword />}
          </Route>
          <Route exact path="/callback/:id">
            <Callback />
          </Route>
          {/* Standalone Stepper route outside of layouts */}
          <Route exact path="/stepper">
            <PrivateRoute component={Stepper} />
          </Route>
          <Route exact path="/confirm-email">
            <PrivateRoute component={ConfirmEmail} />
          </Route>
          <Route exact path="/verify-email">
            <Verification />
          </Route>
          <Route exact path="/404" component={NotFound} />
          <Route exact path="/500" component={InternalError} />
          {project === 'panel' ? (
            <Route path="/">
              {loginState && !userInfo && !isPaymentRoute ? (
                <LightThemeWrapper>
                  <StepperLoading loading={true} />
                </LightThemeWrapper>
              ) : (
                <LayoutPanel userInfo={userInfo}>
                  <Suspense fallback={<LightThemeWrapper><StepperLoading loading={true} /></LightThemeWrapper>}>
                    <Switch>
                      <PrivateRouteAndStepper
                        exact
                        path="/"
                        component={HomePanel}
                      />
                      <PrivateRouteAndStepper
                        exact
                        path="/usage"
                        component={Usage}
                      />
                      <PrivateRouteAndStepper
                        path="/billing"
                        component={Billing}
                      />

                      <PrivateRouteAndStepper
                        exact
                        path="/payment/status/:status"
                        component={PaymentStatus}
                      />
                      <PrivateRouteAndStepper
                        exact
                        path="/payment/:id"
                        component={Payment}
                      />
                      <PrivateRouteAndStepper
                        exact
                        path="/get-more-credits/payment/:id"
                        component={GetMoreCreditsPayment}
                      />
                      <PrivateRouteAndStepper
                        exact
                        path="/manual-invoices"
                        component={ManualInvoices}
                      />

                      <PrivateRouteAndStepper
                        exact
                        path="/profile"
                        component={Profile}
                      />
                      <PrivateRouteAndStepper
                        exact
                        path="/api-info"
                        component={ApiInfo}
                      />
                      <PrivateRouteAndStepper
                        exact
                        path="/basebutton-test"
                        component={BaseButtonTest}
                      />
                      <Route>
                        <Redirect to="/404" />
                      </Route>
                    </Switch>
                  </Suspense>
                </LayoutPanel>
              )}
            </Route>
          ) : project === 'playground' ? (
            <Route
              path={[
                '/',
                '/verify-email',
                '/profile-data',
                '/company-data',
                '/resume-score',
                '/resume-parser',
                '/resume-review',
              ]}
              exact
            >
              {loginState && !userInfo && !isPaymentRoute ? (
                <LightThemeWrapper>
                  <StepperLoading loading={true} />
                </LightThemeWrapper>
              ) : (
                <LayoutPlayground handleCancled={handleCancled}>
                  <PrivateRouteAndStepper
                    exact
                    path="/"
                    component={HomePlayground}
                  />
                  <Suspense fallback={null}>
                    <PrivateRouteAndStepper
                      exact
                      path="/profile-data"
                      component={LinkedinProfile}
                    />
                    <PrivateRouteAndStepper
                      exact
                      path="/company-data"
                      component={LinkedinCompany}
                    />
                    <PrivateRouteAndStepper
                      exact
                      path="/resume-score"
                      component={ResumeScore}
                    />
                    <PrivateRouteAndStepper
                      exact
                      path="/resume-parser"
                      component={ResumeParser}
                    />
                    <PrivateRouteAndStepper
                      exact
                      path="/resume-review"
                      component={ResumeReview}
                    />
                  </Suspense>
                </LayoutPlayground>
              )}
            </Route>
          ) : null}
          <Route path="*">
            <Redirect to="/404" />
          </Route>{' '}
        </Switch>
        <ToastContainer closeButton={false} />
        <MenuHelp />
        {/* <ChristmasPopup /> */}
        <CreditsBalanceModalContainer />
      </CustomTheme>
  );
}

export default App;
